import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from './TopNav';
import { AppSidebar } from './AppSidebar';
import { PersonaProvider } from '@/contexts/PersonaContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { CallProvider } from '@/contexts/CallContext';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { InactivityWarning } from '@/components/auth/InactivityWarning';
import { AIAgentChat } from './AIAgentChat';

const ROLE_TIMEOUTS: Record<string, number> = {
  lawyer: 15 * 60 * 1000,
  agent: 15 * 60 * 1000,
  client: 30 * 60 * 1000,
  superadmin: 15 * 60 * 1000,
};

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const isMobile = useIsMobile();
  const { profile, signOut, user } = useAuth();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);

  const timeoutMs = ROLE_TIMEOUTS[profile?.role || 'client'] ?? ROLE_TIMEOUTS.client;

  const handleSignOut = useCallback(async () => {
    setWarningOpen(false);
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  const handleWarning = useCallback(() => {
    setWarningOpen(true);
  }, []);

  const { resetTimer } = useInactivityTimeout({
    timeoutMs,
    onWarning: handleWarning,
    onTimeout: handleSignOut,
    enabled: !!user,
  });

  const handleStaySignedIn = useCallback(() => {
    setWarningOpen(false);
    resetTimer();
  }, [resetTimer]);

  return (
    <PersonaProvider>
      <CallProvider>
        <div className="min-h-screen bg-background">
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md">
            Skip to main content
          </a>
          <TopNav />
          <div className="flex min-h-[calc(100svh-4rem)] w-full items-stretch">
            {!isMobile && <AppSidebar />}
            <main id="main-content" className="min-h-[calc(100svh-4rem)] flex-1 overflow-auto" role="main">
              {children}
            </main>
          </div>
          <AIAgentChat />
        </div>
      </CallProvider>
      <InactivityWarning
        open={warningOpen}
        onStaySignedIn={handleStaySignedIn}
        onSignOut={handleSignOut}
      />
    </PersonaProvider>
  );
}
